// src/firebase/fcmTokens.js
// ─────────────────────────────────────────────────────────────────────────────
// FCM token management — save/remove push notification tokens in Firestore.
// Tokens are stored in the `fcmTokens` collection, keyed by the token itself.
// ─────────────────────────────────────────────────────────────────────────────

import { doc, setDoc, getDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./config";

/**
 * Save (upsert) an FCM token to Firestore.
 * This is called when a user grants notification permission and we get a token.
 *
 * Preserves the original `createdAt` timestamp if the document already exists,
 * preventing accidental overwrite of the registration date on subsequent saves.
 *
 * @param {string} token - The FCM push token.
 * @param {object} [meta] - Optional metadata (e.g. { uid, label }).
 */
export async function saveFcmToken(token, meta = {}) {
  if (!token) return;

  console.log("[FCM] Saving token to Firestore:", token, meta);

  // Check if the document already exists to preserve the original createdAt
  const ref = doc(db, "fcmTokens", token);
  const existingSnap = await getDoc(ref);

  const data = {
    token,
    userAgent: navigator.userAgent || "",
    updatedAt: serverTimestamp(),
    ...meta,
  };

  if (existingSnap.exists()) {
    // Preserve original createdAt, only update updatedAt and userAgent
    data.createdAt = existingSnap.data().createdAt;
    await setDoc(ref, data, { merge: true });
  } else {
    // New token — set both createdAt and updatedAt
    data.createdAt = serverTimestamp();
    await setDoc(ref, data);
  }
}

/**
 * Remove an FCM token from Firestore.
 * Called when a user revokes permission or on logout.
 *
 * @param {string} token
 */
export async function removeFcmToken(token) {
  if (!token) return;
  await deleteDoc(doc(db, "fcmTokens", token));
}
